import { getTeacherResources } from '../../common/utils'

// The educator-resources listing takes the server seconds to build, so it is
// fetched once and shared: Root primes it the moment the teacher is known,
// and the page joins whatever is already in flight instead of starting over.
let cache = { key: null, promise: null, data: null, at: 0 }

const keyFor = user => user.userId || user.uid || user.email || 'user'

// Data younger than this is trusted as-is, so flipping between dashboard
// sections is free. Older data still paints instantly but triggers a
// background refresh - that is how a license gained or lost shows up.
const FRESH_MS = 60 * 1000

const STORAGE_KEY = 'teacherResourcesListing:v1'

/** Last successful listing for this user from localStorage, else null. */
function storedListing(user) {
  if (!user) {
    return null
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    return parsed && parsed.key === keyFor(user) ? parsed.data : null
  } catch (err) {
    return null
  }
}

function writeStored(key, data) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ key, data, at: Date.now() }))
  } catch (err) {
    // Quota or private mode - the stored copy is a bonus, not a requirement.
  }
}

/** The listing if it is already in hand for this user, else null. */
function peekListing(user) {
  if (!user) {
    return null
  }
  return cache.key === keyFor(user) ? cache.data : null
}

/**
 * Start (or join) the listing fetch for this user. Safe to call early and
 * often: at most one request is in flight, and every caller shares it.
 * Failures clear the slot so the next call retries.
 */
function startFetch(key, user) {
  const promise = (async () => {
    const resp = await getTeacherResources('list', user)
    if (!resp || typeof resp.json !== 'function' || !resp.ok) {
      throw new Error('listing fetch failed')
    }
    const data = await resp.json()
    cache = { key, promise: null, data, at: Date.now() }
    writeStored(key, data)
    return data
  })()
  // A revalidation keeps serving the copy already in hand until the fresh
  // one lands; a failure must neither poison that copy nor surface an
  // unhandled rejection from a fire-and-forget caller (Root's warm-up).
  cache = {
    key,
    promise,
    data: cache.key === key ? cache.data : null,
    at: cache.key === key ? cache.at : 0,
  }
  promise.catch(() => {
    if (cache.promise === promise) {
      cache = { ...cache, promise: null }
    }
  })
  return promise
}

/**
 * Start (or join) the listing fetch for this user. Safe to call early and
 * often: at most one request is in flight, and every caller shares it.
 * Returns whatever is already in hand without refetching.
 */
function primeListing(user) {
  if (!user) {
    return Promise.reject(new Error('not logged in'))
  }
  const key = keyFor(user)
  if (cache.key === key) {
    if (cache.data) {
      return Promise.resolve(cache.data)
    }
    if (cache.promise) {
      return cache.promise
    }
  }
  return startFetch(key, user)
}

/**
 * Revalidate the listing: paint-from-cache callers use this to fetch a
 * fresh copy in the background. Joins any in-flight request; data younger
 * than FRESH_MS is trusted as-is and returns null (nothing to wait for).
 */
function refreshListing(user) {
  if (!user) {
    return null
  }
  const key = keyFor(user)
  if (cache.key === key) {
    if (cache.promise) {
      return cache.promise
    }
    if (cache.data && Date.now() - cache.at < FRESH_MS) {
      return null
    }
  }
  return startFetch(key, user)
}

// Tests render the page many times inside one module instance.
function _resetListingCacheForTests() {
  cache = { key: null, promise: null, data: null, at: 0 }
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch (err) {
    // jsdom always has localStorage; belt and braces.
  }
}

export { primeListing, refreshListing, peekListing, storedListing, _resetListingCacheForTests }
